import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Link2, RefreshCw, Zap, Radio, Wifi } from 'lucide-react';
import { useMatchStats, useAutoMatch } from '../hooks/useSimCards';

export function SimMatchingPanel() {
  const matchStatsQuery = useMatchStats();
  const autoMatchMutation = useAutoMatch();

  const stats = matchStatsQuery.data;
  const isLoading = matchStatsQuery.isLoading;

  const handleAutoMatch = () => {
    autoMatchMutation.mutate();
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              SIM-Transmitter Matching
            </CardTitle>
            <CardDescription>
              Match SIM cards to transmitters by ICCID, IMEI, or MSISDN
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => matchStatsQuery.refetch()}
              disabled={matchStatsQuery.isFetching}
              title="Refresh SIM-to-transmitter matching statistics"
              aria-label="Refresh SIM-to-transmitter matching statistics"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${matchStatsQuery.isFetching ? 'animate-spin' : ''}`} />
              Refresh Match Stats
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  disabled={autoMatchMutation.isPending || (stats?.unmatched_sims === 0)}
                >
                  <Zap className={`h-4 w-4 mr-2 ${autoMatchMutation.isPending ? 'animate-pulse' : ''}`} />
                  {autoMatchMutation.isPending ? 'Matching...' : 'Run Auto-Match'}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Run Auto-Match?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will attempt to match unmatched SIM cards to transmitters by comparing:
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>ICCID (highest priority)</li>
                      <li>IMEI</li>
                      <li>MSISDN/Phone number</li>
                    </ul>
                    <p className="mt-2">
                      {stats?.unmatched_sims || 0} unmatched SIM{(stats?.unmatched_sims || 0) !== 1 ? 's' : ''} will be processed.
                    </p>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleAutoMatch}>
                    Run Auto-Match
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2">
          {/* SIM Cards Stats */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Wifi className="h-4 w-4" />
              SIM Cards
            </h4>
            <div className="grid grid-cols-3 gap-4">
              <StatBox
                label="Total"
                value={stats?.total_sims}
                isLoading={isLoading}
              />
              <StatBox
                label="Matched"
                value={stats?.matched_sims}
                isLoading={isLoading}
                valueClassName="text-green-600"
              />
              <StatBox
                label="Unmatched"
                value={stats?.unmatched_sims}
                isLoading={isLoading}
                valueClassName={stats?.unmatched_sims ? 'text-orange-600' : undefined}
              />
            </div>
            {/* Match rate bar */}
            {stats && stats.total_sims > 0 && (
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Match Rate</span>
                  <span>{Math.round((stats.matched_sims / stats.total_sims) * 100)}%</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500 transition-all duration-500"
                    style={{ width: `${(stats.matched_sims / stats.total_sims) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Transmitters Stats */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Radio className="h-4 w-4" />
              Transmitters
            </h4>
            <div className="grid grid-cols-3 gap-4">
              <StatBox
                label="Total"
                value={stats?.total_transmitters}
                isLoading={isLoading}
              />
              <StatBox
                label="With SIM"
                value={stats?.matched_transmitters}
                isLoading={isLoading}
                valueClassName="text-green-600"
              />
              <StatBox
                label="No SIM"
                value={stats?.unmatched_transmitters}
                isLoading={isLoading}
                valueClassName={stats?.unmatched_transmitters ? 'text-gray-500' : undefined}
              />
            </div>
            {/* Match rate bar */}
            {stats && stats.total_transmitters > 0 && (
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Coverage</span>
                  <span>{Math.round((stats.matched_transmitters / stats.total_transmitters) * 100)}%</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 transition-all duration-500"
                    style={{ width: `${(stats.matched_transmitters / stats.total_transmitters) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Last match result */}
        {autoMatchMutation.isSuccess && autoMatchMutation.data && (
          <div className="mt-4 p-3 bg-muted rounded-lg">
            <p className="text-sm">
              <span className="font-medium">Last auto-match result:</span>{' '}
              <span className="text-green-600">{autoMatchMutation.data.newly_matched} matched</span>,{' '}
              <span className="text-muted-foreground">{autoMatchMutation.data.unmatched} unmatched</span>
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Helper Components
// =============================================================================

interface StatBoxProps {
  label: string;
  value?: number;
  isLoading?: boolean;
  valueClassName?: string;
}

function StatBox({ label, value, isLoading, valueClassName }: StatBoxProps) {
  return (
    <div className="text-center">
      {isLoading ? (
        <Skeleton className="h-8 w-12 mx-auto" />
      ) : (
        <p className={`text-2xl font-bold ${valueClassName || ''}`}>
          {value ?? 0}
        </p>
      )}
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}
